import { parseShowroomWorkerBuildingGenOverride } from "common/utils/showroomWorkerBuildingGenQuery";
import { useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { isShowroomFakeId } from "lib/utils/showroom/showroomData";
import type { PlanIdentifiers } from "lib/workers/showroomBuildingWorkerMessages";
import { useMemo } from "react";
import useShowroomState from "../state/showroomStateContext";

// Edit mode (EDIT/PREVIEW) must use the main-thread generation path. Worker and server
// generation apply persisted plan overrides internally, but edit-mode overrides live in
// patchedPlanData. Override-only edits neither reach those paths nor trigger regeneration,
// so they would render stale data and leave the Configure Options "Done" state waiting
// indefinitely.
export function useIsShowroomEditMode(): boolean {
  const [mode] = useShowroomState("mode");
  return mode !== null;
}

function getNormalizedRenderSource(renderSource: string | string[] | undefined): string | undefined {
  const renderSourceValue = Array.isArray(renderSource) ? renderSource[0] : renderSource;
  return renderSourceValue?.toLowerCase();
}

function getShowroomWorkerBuildingGenUrlOverride(): boolean | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  return parseShowroomWorkerBuildingGenOverride(window.location.search);
}

export function useIsShowroomServerMode(): boolean {
  const { renderSource } = useAppRouterParams();
  const isEditMode = useIsShowroomEditMode();
  if (isEditMode) {
    return false;
  }
  return getNormalizedRenderSource(renderSource) === "server";
}

/**
 * Worker mode is on when the URL override (`?showroomWorkerBuildingGen=1`) or the feature
 * flag enables it, and server mode is off (server mode wins).
 */
export function useIsShowroomWorkerMode(): boolean {
  const isServerMode = useIsShowroomServerMode();
  const isEditMode = useIsShowroomEditMode();
  const { enabledFeatures } = useUserContext();
  const override = getShowroomWorkerBuildingGenUrlOverride();
  if (isServerMode || isEditMode) {
    return false;
  }

  // `override` is intentionally tri-state: true/false explicitly force the mode,
  // while undefined means no valid URL override and falls back to the feature flag.
  if (override !== undefined) {
    return override;
  }

  return enabledFeatures?.showroomWorkerBuildingGen ?? false;
}

export function getShowroomWorkerPlanIdentifiers(
  builderId: string | undefined,
  planType: PlanIdentifiers["planType"] | undefined,
  planId: string | undefined,
  lotId: string | undefined,
): PlanIdentifiers | undefined {
  if (!builderId || !planId) {
    return undefined;
  }

  if (isShowroomFakeId(builderId) || isShowroomFakeId(planId) || isShowroomFakeId(lotId)) {
    return undefined;
  }

  return {
    builderId,
    planType: planType ?? "community_plans",
    planId,
    lotId,
  };
}

export function useShowroomWorkerPlanIdentifiers(): PlanIdentifiers | undefined {
  const { builderId, planType = "community_plans", planId, lotId } = useAppRouterParams();
  return useMemo(
    () => getShowroomWorkerPlanIdentifiers(builderId, planType, planId, lotId),
    [builderId, planType, planId, lotId],
  );
}
